import { useParticipantGroupApi } from '@api/domains/participant-group';
import { useSurveyDocumentGroupsApi } from '@api/domains/survey-document-groups';
import { useLang } from '@common/context/language-provider';
import { checkIfAdminRoute } from '@common/utils/check-if-admin-route';
import { Button } from '@konova/ui/button';
import { ButtonGroup } from '@konova/ui/button-group';
import { InfoBox } from '@konova/ui/info-box';
import { Typography } from '@konova/ui/typography';
import { useConfirmation } from '@konova/ui/use-confirmation';
import { ISurvey, IUser } from '@legacy/types';
import { SpaceRoutes } from '@routes/space-routes';
import { SurveyRoutes } from '@routes/survey-routes';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router';

type ContextSwitcherButtonProps = {
  survey: ISurvey | null;
  user?: IUser | null;
};

export const ContextSwitcherButton: FC<ContextSwitcherButtonProps> = ({
  survey,
  user,
}) => {
  const [isLoading, setLoading] = useState(false);

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { language: lang } = useLang();
  const surveyDocumentGroupsApi = useSurveyDocumentGroupsApi();
  const participantGroupApi = useParticipantGroupApi();

  const confirmation = useConfirmation({
    title: t('components.context-button.confirmation.title'),
    description: (
      <>
        <InfoBox>{t('components.context-button.confirmation.info')}</InfoBox>
        <Typography as="p">
          {t('components.context-button.confirmation.description')}
        </Typography>
      </>
    ),
    confirmButton: <Button>{t('components.context-button.confirmation.button')}</Button>,
    onConfirm: () => {
      const url = generatePath(SurveyRoutes.setupDocumentGroups, {
        lang: lang ?? 'de',
        surveySlug: survey?.slug ?? '/',
      });
      navigate(url);
    },
  });

  const isAdminRoute = useMemo(
    () => checkIfAdminRoute(location.pathname),
    [location.pathname]
  );

  const getAdminUrl = () => {
    if (!survey) return '/';

    const matchDocumentGroup = matchPath(SurveyRoutes.documentGroup, location.pathname);
    const matchInfoDocumentGroup = matchPath(SurveyRoutes.infoDocumentGroup, location.pathname);

    const id = matchDocumentGroup?.params.id ?? matchInfoDocumentGroup?.params.id;
    if (id) {
      return generatePath(SurveyRoutes.AdminSetupSurveyDocumentGroupEdit, {
        id,
        lang: lang ?? 'de',
        surveySlug: survey.slug,
      });
    }

    const match = matchPath(SurveyRoutes.participant, location.pathname);
    if (match?.pattern.end) {
      return generatePath(SurveyRoutes.setupDocumentGroups, {
        lang: lang ?? 'de',
        surveySlug: survey.slug,
      });
    }

    return generatePath(SurveyRoutes.admin, {
      lang: lang ?? 'de',
      surveySlug: survey.slug,
    });
  };

  const getParticipantUrl = async () => {
    if (!survey) return '/';

    const match = matchPath(SurveyRoutes.AdminSetupSurveyDocumentGroupEdit, location.pathname);
    if (match && match.params.id) {
      const docGroup = await surveyDocumentGroupsApi.getById(Number(match.params.id));
      return generatePath(
        docGroup.show_in_info_section ? SurveyRoutes.infoDocumentGroup : SurveyRoutes.documentGroup,
        {
          id: docGroup.id.toString(),
          lang: lang ?? 'de',
          surveySlug: survey.slug,
        }
      );
    }

    return generatePath(SurveyRoutes.participant, {
      lang: lang ?? 'de',
      surveySlug: survey.slug,
    });
  };

  const clickHandler = async () => {
    setLoading(true);
    if (isAdminRoute && survey && survey.id) {
      if (!survey.activeStage) {
        confirmation.show();
        setLoading(false);
        return;
      }

      await participantGroupApi.createGroupForAdmin(survey.id);
    }
    const url = isAdminRoute ? await getParticipantUrl() : getAdminUrl();
    setLoading(false);
    navigate(url);
  };

  const clickPanelHandler = () => {
    const url = generatePath(SpaceRoutes.admin, {
      lang: lang ?? 'de',
    });
    navigate(url);
  };

  return (
    <ButtonGroup variant="outlined">
      <Button size="small" loading={isLoading} onClick={() => clickHandler()}>
        {isAdminRoute ? t('components.context-button.preview') : t('components.context-button.admin')}
      </Button>
      {user?.spaceUser?.canAccessPanel && isAdminRoute && (
        <Button size="small" onClick={() => clickPanelHandler()}>
          {t('components.context-button.panel')}
        </Button>
      )}
      {confirmation.component}
    </ButtonGroup>
  );
};